// FeedView.jsx

import React, { useMemo, useCallback } from 'react';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Utilities
import { isOwnerPrivateNode } from 'utilities/nodeAccessScope';

// CSS
import './FeedView.css';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import FeedViewItem from './item/FeedViewItem';

const pickNodeKey = (node) => {
  if (!node) return '';
  return node.key?.trim?.() || '';
};

const encodeDataKey = (key) => {
  if (!key) return '';
  try {
    return encodeURIComponent(key);
  } catch (error) {
    return key;
  }
};

const coerceTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds || 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  if (typeof value._seconds === 'number') {
    const nanos = typeof value._nanoseconds === 'number' ? value._nanoseconds : 0;
    return value._seconds * 1000 + Math.floor(nanos / 1e6);
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : 0;
  }
  return 0;
};

const getNodeTimestampMillis = (node) => {
  if (!node) return 0;
  return (
    coerceTimestampMillis(node.dateModified) ||
    coerceTimestampMillis(node.dateCreated)
  );
};

/**
 * FeedView
 *
 * @returns {ReactNode} The rendered component.
 */
const FeedView = React.memo(({
  view = null,
  typeDef: _typeDef,
  nodes = [],
  language: _language = 'en',
  maxNodes = null,
  compact = false,
  shareSelection = null,
}) => {

  // ----- HOOKS ----- //

  const { openNode } = useNodeOpenIntent();

  // ----- MEMOS ----- //

  const orderedItems = useMemo(() => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return [];
    }
    const deduped = [];
    const seen = new Set();
    nodes.forEach((item) => {
      if (!item) return;
      const key = pickNodeKey(item);
      if (key && seen.has(key)) return;
      if (key) {
        seen.add(key);
      }
      deduped.push(item);
    });
    return deduped;
  }, [nodes]);

  /**
   * Filter out certain nodes from the feed for better UX.
   */
  const visibleNodes = useMemo(() => {
    if (!Array.isArray(orderedItems) || orderedItems.length === 0) {
      return orderedItems;
    }

    return orderedItems.filter((node) => {
      if (!node) return false;

      const typeKey = node?.type;
      if (typeKey !== 'PHOTO') {
        return true;
      }

      // Skip photos whose parent node is a gallery to avoid duplicate feed entries.
      const parentType = node?.parentType;
      return parentType !== 'GALLERY';
    });
  }, [orderedItems]);

  const displayedNodes = useMemo(() => {
    if (!Number.isFinite(maxNodes) || maxNodes <= 0) {
      return visibleNodes;
    }
    return visibleNodes.slice(0, maxNodes);
  }, [visibleNodes, maxNodes]);

  const shareEnabled = Boolean(shareSelection?.enabled);
  const selectedKeys = shareSelection?.selectedKeys;

  // ----- HANDLERS ----- //

  const handleOpenNode = useCallback((node) => {
    if (!node?.key) return;
    openNode(node, { preferPeek: true });
  }, [openNode]);

  if (displayedNodes.length === 0) {

    return (

      <div className="feed-view feed-view--empty">

        {/* EMPTY STATE */}
        <div
          className="feed-view-empty-state"
        >
          Nothing here yet. Check back soon.
        </div>
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="feed-view-container">
      {displayedNodes.map((node) => {
        const rawKey = pickNodeKey(node) || `${node?.type || 'NODE'}-${getNodeTimestampMillis(node)}`;
        const dataKey = encodeDataKey(rawKey);
        const isEmailThread = String(node?.type || '').toUpperCase() === 'EMAIL_THREAD';
        const showShareControls = shareEnabled && isEmailThread;
        const isSelected = Boolean(
          showShareControls
          && selectedKeys
          && typeof selectedKeys.has === 'function'
          && selectedKeys.has(rawKey)
        );
        const shareBadge = showShareControls
          ? (isOwnerPrivateNode(node) ? 'Only you' : 'Shared')
          : null;

        return (
          <div
            className={[
              'feed-view-row',
              shareEnabled ? 'feed-view-row--share' : '',
            ].filter(Boolean).join(' ')}
            data-node-key={dataKey}
            key={rawKey}
          >
            {shareEnabled ? (
              <div
                className="feed-view-row-check"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                role="presentation"
              >
                {showShareControls ? (
                  <Checkbox
                    size={22}
                    value={isSelected}
                    onChange={() => {
                      if (typeof shareSelection?.onToggle === 'function') {
                        shareSelection.onToggle(rawKey);
                      }
                    }}
                  />
                ) : (
                  <span className="feed-view-row-check-spacer" aria-hidden="true" />
                )}
              </div>
            ) : null}
            <div className="feed-view-row-item">
              <FeedViewItem
                node={node}
                view={view}
                compact={compact}
                onClick={handleOpenNode}
                shareBadge={shareBadge}
              />
            </div>
          </div>
        );
      })}

    </div>
  );
});

// Dev Tools
FeedView.displayName = 'FeedView';

export default FeedView;
